import { useWindowSize } from '@openx/utils/hooks/useWindowSize.ts';
import { ColumnsIcon } from '@phosphor-icons/react/Columns';
import isEqual from 'lodash/isEqual';
import { memo, type ReactElement, useMemo, useState } from 'react';
import {
  ListItemFieldsFilter,
  type ListItemFieldsFilterProps,
} from '../List/filters/ListItemFieldsFilter/ListItemFieldsFilter.tsx';
import { List } from '../List/List.tsx';
import type { ListTopBarProps, VirtualizedListProps } from '../List/types.ts';
import { TRANSFER_LIST_MIN_SEARCH_PHRASE_LENGTH, TRANSFER_LIST_READ_ONLY_LIST_HEIGHT } from './constants.ts';
import { useTransferListItemFields } from './hooks/useTransferListItemFields.ts';
import { useTransferListItemSizes } from './hooks/useTransferListItemSizes.ts';
import type { TransferListBaseItem, TransferListItemProps } from './types.ts';

export type TransferListReadOnlyProps<TItem extends TransferListBaseItem> = {
  ListItemComponent: VirtualizedListProps<TItem, TransferListItemProps<TItem>>['ListItemComponent'];
  dataTest?: string;
  fields: {
    config: ListItemFieldsFilterProps<TItem>['fields'];
    localStorageKey: string;
  };
  isLoading?: boolean;
  items: TItem[];
  onSearch: (item: TItem, searchPhrase: string) => boolean;
  searchBarPlaceholder?: ListTopBarProps['searchBarPlaceholder'];
};

export const TransferListReadOnly = memo(
  <TItem extends TransferListBaseItem>({
    ListItemComponent,
    dataTest,
    fields,
    isLoading,
    items,
    onSearch,
    searchBarPlaceholder,
  }: TransferListReadOnlyProps<TItem>) => {
    const [fieldsFilterAnchor, setFieldsFilterAnchor] = useState<HTMLElement | null>(null);
    const [searchPhrase, setSearchPhrase] = useState('');

    const { fieldsConfig, handleFieldFilterClick, handleFieldFilterOrderChange } = useTransferListItemFields(fields);
    const { getItemSize, setItemSize } = useTransferListItemSizes();
    const { width: windowWidth } = useWindowSize();

    const filteredItems = useMemo<TItem[]>(() => {
      if (searchPhrase.length >= TRANSFER_LIST_MIN_SEARCH_PHRASE_LENGTH) {
        return items
          .filter(item => onSearch(item, searchPhrase))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }

      return items;
    }, [items, onSearch, searchPhrase]);

    const listItemProps = useMemo<TransferListItemProps<TItem>>(
      () => ({
        fields: fieldsConfig,
        itemsSelected: {},
        searchPhrase,
        setItemSize,
        windowWidth,
      }),
      [fieldsConfig, searchPhrase, setItemSize, windowWidth]
    );

    return (
      <>
        <List.Root dataTest={dataTest}>
          <List.TopBar
            options={[
              {
                Icon: ColumnsIcon,
                name: 'Manage Info*',
                onClick: anchor => setFieldsFilterAnchor(anchor),
              },
            ]}
            searchBarPlaceholder={searchBarPlaceholder}
            searchPhrase={searchPhrase}
            setSearchPhrase={setSearchPhrase}
          />

          <List.Divider />

          <List.HeaderActions
            itemsCount={filteredItems.length}
            itemsText={`${filteredItems.length} ${filteredItems.length !== 1 ? 'items' : 'item'}`}
            selectedItemsCount={0}
          />

          <List.Divider />

          <List.Content<TItem, TransferListItemProps<TItem>>
            data={filteredItems}
            height={TRANSFER_LIST_READ_ONLY_LIST_HEIGHT}
            isLoading={isLoading}
            itemSize={getItemSize}
            ListItemComponent={ListItemComponent}
            listItemProps={listItemProps}
          />
        </List.Root>

        <ListItemFieldsFilter<TItem>
          anchor={fieldsFilterAnchor}
          fields={fieldsConfig}
          onClick={handleFieldFilterClick}
          onOrderChange={handleFieldFilterOrderChange}
          setAnchor={setFieldsFilterAnchor}
        />
      </>
    );
  },
  (prevProps, nextProps) => {
    const hasSameLoadingState = prevProps.isLoading === nextProps.isLoading;
    const hasSameItems = isEqual(prevProps.items, nextProps.items);

    return hasSameLoadingState && hasSameItems;
  }
) as <TItem extends TransferListBaseItem>(props: TransferListReadOnlyProps<TItem>) => ReactElement;
